import React, { useState, useRef, useEffect, useContext } from 'react';
import { navigate } from 'hookrouter';
import GlobalSearchDropdown from './shared/GlobalSearchDropdown';
import SpinLoader from './shared/field_inputs/shared/SpinLoader';
import { focusRef, useDebounce, scrollToElement } from './shared/field_inputs/shared/EventHelpers';
import { GlobalSearchReq } from './requests/GlobalSearchRequests.js';
import { getIcon } from './shared/field_inputs/shared/FormatHelpers';
import { DataContext as SharedDataContext } from '../../stores/crm-store';

const GlobalSearchBox = ({ onKeyDown: _onKeyDown, isInvalid, autoFocus }) => {
    const inputRef = useRef();

    const { store: sharedStore, dispatch: sharedDispatch } = useContext(SharedDataContext);

    const categories = sharedStore.searchCategories;
    const [category, setCategory] = useState(categories[0]);

    const getSuggestions = GlobalSearchReq(category.index);

    const [isLoading, setIsLoading] = useState(false);
    const [userInput, setUserInput] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const maxIndex = suggestions ? suggestions.length - 1 : 0;
    const icon = getIcon('search');
    const maxSuggestionsToShow = 50;

    useEffect(() => {
        autoFocus && focusRef(inputRef);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const debouncedInput = useDebounce(userInput, 500);
    useEffect(() => {
        if (debouncedInput) {
            const updateSuggestionsAsync = async () => {
                setIsLoading(true);
                if (getSuggestions) setSuggestions(await getSuggestions(debouncedInput));
                setShowSuggestions(true);
                setIsLoading(false);
            };
            updateSuggestionsAsync().then(() => {
                var suggestionsScroll = document.getElementById('suggestions');
                suggestionsScroll.scrollTop = 0;
            });
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
            setCategory(categories[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedInput, category]);

    const navigateToSelectedSuggestion = (event) => {
        setUserInput('');
        const link = suggestions[activeIndex].itemLink;
        const anchor = link.includes('#') ? link.substring(link.indexOf('#') + 1) : null;
        sharedDispatch({ type: 'SET_ANCHOR_TAG', payload: anchor });
        navigate(suggestions[activeIndex].itemLink);
        event.preventDefault();
    };

    const onMouseDown = (event) => {
        navigateToSelectedSuggestion(event);
    };

    const onKeyDown = (event) => {
        // If empty field, call parent method
        if (!userInput || !userInput.length) {
            _onKeyDown && _onKeyDown(event);
        }

        // Enter adds selected item
        if (event.keyCode === 13) {
            if (activeIndex >= 0 && activeIndex <= maxIndex) {
                navigateToSelectedSuggestion(event);
            }
            event.preventDefault();
        }

        // Up-arrow moves up in list
        else if (event.keyCode === 38) {
            if (activeIndex > -1) {
                setActiveIndex(activeIndex - 1);
                scrollToElement(`suggestion-${activeIndex - 1}`);
            }
            event.preventDefault();
        }

        // Down-arrow moves down in list
        else if (event.keyCode === 40) {
            if (activeIndex < maxIndex) {
                setActiveIndex(activeIndex + 1);
                scrollToElement(`suggestion-${activeIndex + 1}`);
            }
            event.preventDefault();
        }
    };

    const onFileDownload = async (item) => {
        var splitted = item.itemLink.split('file-');
        if (splitted[1]) {
            var fileId = splitted[1];
            let url = `api/crmFileFront/${fileId}/download`;
            var link = document.createElement('a');
            link.setAttribute('target', '_blank');
            link.download = item.title;
            link.href = url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const onBlur = (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setShowSuggestions(false);
        }
    };

    const onFocus = () => {
        if (userInput.length) {
            setShowSuggestions(true);
        }
    };

    return (
        <div className='crm-editable-field-wrapper'>
            <div className={`input-wrapper ${isInvalid ? 'invalid' : ''}`} onClick={() => focusRef(inputRef)}>
                <div className='new-item-selector' onBlur={onBlur} tabIndex='0'>
                    <div className='d-flex flex-nowrap'>
                        <SpinLoader isLoading={isLoading} />
                        {icon}
                        <input
                            ref={inputRef}
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyDown={onKeyDown}
                            onFocus={onFocus}
                            placeholder={'Sök'}
                        />
                    </div>
                    {showSuggestions ? (
                        <GlobalSearchDropdown
                            {...{
                                debouncedInput,
                                suggestions,
                                showSuggestions,
                                setActiveIndex,
                                onMouseDown,
                                maxSuggestionsToShow,
                                categories,
                                category,
                                setCategory,
                                isLoading,
                                activeIndex,
                                onFileDownload,
                            }}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default GlobalSearchBox;
